import React, { useEffect, useRef } from 'react';
import { auraStickerHoverRef } from '../../auraStickerHoverRef';
import '../../styles/ARBackground.css';

function smoothstep(t) {
  const x = Math.min(1, Math.max(0, t));
  return x * x * (3 - 2 * x);
}

function lerp(a, b, t) {
  return a + (b - a) * t;
}

function rgba(r, g, b, a) {
  return `rgba(${Math.round(r)},${Math.round(g)},${Math.round(b)},${a})`;
}

/**
 * @param {{ scrollProgressRef?: React.MutableRefObject<number> | null }} props
 * scrollProgressRef: 0~1, 메인 등에서 window 스크롤로 갱신. 없으면 항상 0 (기존 페이지 동작).
 */
const ARBackground = ({ scrollProgressRef = null }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d', { alpha: true, desynchronized: true });

    let cancelled = false;
    let rafId = 0;

    const particles = [];
    const particleCount = 52;

    const waves = [];
    for (let i = 0; i < 3; i++) {
      waves.push({
        y: 0,
        amplitude: 50 + i * 20,
        frequency: 0.01 + i * 0.005,
        speed: 0.02 + i * 0.01,
        opacity: 0.1 - i * 0.02,
      });
    }

    let bins = [];
    let bw = 0;
    let bh = 0;
    const cellSize = 96;

    function rebuildBinsDimensions() {
      bw = Math.max(1, Math.ceil(canvas.width / cellSize));
      bh = Math.max(1, Math.ceil(canvas.height / cellSize));
      bins = new Array(bw * bh);
      for (let i = 0; i < bins.length; i++) bins[i] = [];
    }

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      rebuildBinsDimensions();
      waves.forEach((wave, i) => {
        wave.y = canvas.height / 2 + i * 100;
      });
      for (let i = 0; i < particles.length; i++) {
        if (!particles[i]) {
          particles[i] = {
            x: Math.random() * canvas.width,
            y: Math.random() * canvas.height,
            vx: (Math.random() - 0.5) * 0.5,
            vy: (Math.random() - 0.5) * 0.5,
            size: Math.random() * 3 + 1,
            opacity: Math.random() * 0.5 + 0.1,
          };
        } else {
          particles[i].x = Math.min(
            canvas.width,
            Math.max(0, particles[i].x)
          );
          particles[i].y = Math.min(
            canvas.height,
            Math.max(0, particles[i].y)
          );
        }
      }
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    for (let i = particles.length; i < particleCount; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        size: Math.random() * 3 + 1,
        opacity: Math.random() * 0.5 + 0.1,
      });
    }

    let time = 0;
    /* 스크롤 ref 값 급변 → 캔버스는 매 프레임 부드럽게 따라감 (보라색 번쩍임 방지) */
    let smoothScroll01 = 0;
    let smoothStickerHover = 0;

    const animate = () => {
      if (cancelled) return;

      const targetP = Math.min(
        1,
        Math.max(0, scrollProgressRef?.current ?? 0)
      );
      smoothScroll01 += (targetP - smoothScroll01) * 0.098;
      if (Math.abs(targetP - smoothScroll01) < 0.0004) smoothScroll01 = targetP;

      const stickerTarget = auraStickerHoverRef.current;
      smoothStickerHover += (stickerTarget - smoothStickerHover) * 0.18;
      if (Math.abs(stickerTarget - smoothStickerHover) < 0.0005) {
        smoothStickerHover = stickerTarget;
      }
      const stick = smoothStickerHover;

      /* 같은 스크롤 양에서 보라 톤이 더 이르게 차오르도록 상한 1로 클램프 */
      const boosted = Math.min(1, smoothScroll01 * 1.4);
      const u = smoothstep(boosted);

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const cx = canvas.width * 0.5;
      const cy = canvas.height * 0.5;
      const zoom = 1 + stick * 0.068;
      ctx.save();
      ctx.translate(cx, cy);
      ctx.scale(zoom, zoom);
      ctx.rotate(stick * 0.014 * Math.sin(time * 0.0026));
      ctx.translate(-cx, -cy);

      /* 색 wash: 처음부터 알파만 u로 서서히 (if 임계 제거) */
      const washA = u * 0.22;
      if (washA > 0.001) {
        const wash = ctx.createLinearGradient(
          0,
          canvas.height * 0.15,
          canvas.width,
          canvas.height * 0.9
        );
        wash.addColorStop(
          0,
          rgba(
            lerp(74, 68, u),
            lerp(144, 95, u),
            lerp(226, 218, u),
            washA * 0.42
          )
        );
        wash.addColorStop(
          0.4,
          rgba(
            lerp(123, 150, u),
            lerp(104, 88, u),
            lerp(238, 235, u),
            washA * 0.55
          )
        );
        wash.addColorStop(
          1,
          rgba(
            lerp(74, 62, u),
            lerp(144, 105, u),
            lerp(226, 195, u),
            washA * 0.38
          )
        );
        ctx.fillStyle = wash;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }

      /* 우상단 핑크·보라 글로우: 0.05~0.65 구간에서 서서히 (기존 drama>0.25 툭 켜짐 제거) */
      const glowMix = smoothstep((u - 0.02) / 0.48);
      if (glowMix > 0.001) {
        const pulse =
          0.12 *
          glowMix *
          (0.55 + 0.45 * Math.sin(time * 0.0018));
        const g2 = ctx.createRadialGradient(
          canvas.width * 0.85,
          canvas.height * 0.15,
          0,
          canvas.width * 0.85,
          canvas.height * 0.15,
          canvas.height * 0.55
        );
        g2.addColorStop(0, rgba(236, 72, 153, pulse));
        g2.addColorStop(0.45, rgba(90, 66, 212, pulse * 0.42));
        g2.addColorStop(1, rgba(255, 255, 255, 0));
        ctx.fillStyle = g2;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }

      /* 초기(u≈0)는 살짝 베이지, 스크롤 진행 시 기존 밝은·보라 톤으로 수렴 */
      const beige0 = { r: 252, g: 246, b: 236 };
      const beige1 = { r: 245, g: 238, b: 226 };
      const beige2 = { r: 236, g: 230, b: 218 };
      const midR = lerp(250, 246, u);
      const midG = lerp(252, 235, u);
      const midB = lerp(255, 255, u);
      const outR = lerp(240, 228, u);
      const outG = lerp(248, 232, u);
      const outB = lerp(255, 252, u);

      const gradient = ctx.createRadialGradient(
        canvas.width * lerp(0.5, 0.4, u),
        canvas.height * lerp(0.5, 0.56, u),
        0,
        canvas.width / 2,
        canvas.height / 2,
        canvas.width * 0.55
      );
      gradient.addColorStop(
        0,
        rgba(
          lerp(beige0.r, 255, u),
          lerp(beige0.g, 255, u),
          lerp(beige0.b, 255, u),
          0.06 + u * 0.1
        )
      );
      gradient.addColorStop(
        0.55,
        rgba(
          lerp(beige1.r, midR, u),
          lerp(beige1.g, midG, u),
          lerp(beige1.b, midB, u),
          0.04 + u * 0.07
        )
      );
      gradient.addColorStop(
        1,
        rgba(
          lerp(beige2.r, outR, u),
          lerp(beige2.g, outG, u),
          lerp(beige2.b, outB, u),
          0.02 + u * 0.09
        )
      );
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      /* 스크롤 초반만: 와이어 등각 느낌 + 얕은 타원 (추가 드로우 최소) */
      const intro3dMix = 1 - smoothstep((u - 0.03) / 0.32);
      if (intro3dMix > 0.018) {
        const warmStroke = (a) =>
          rgba(
            lerp(112, 74, u),
            lerp(104, 144, u),
            lerp(94, 226, u),
            a * intro3dMix
          );
        const drawIsoTile = (px, py, w, h, rot, phase) => {
          ctx.save();
          ctx.translate(px, py);
          ctx.rotate(rot + time * phase);
          const k = 0.45;
          ctx.beginPath();
          ctx.moveTo(0, -h * 0.5);
          ctx.lineTo(w * k, 0);
          ctx.lineTo(0, h * 0.5);
          ctx.lineTo(-w * k, 0);
          ctx.closePath();
          ctx.strokeStyle = warmStroke(0.078);
          ctx.lineWidth = 1;
          ctx.stroke();
          ctx.beginPath();
          ctx.moveTo(-w * k * 0.55, -h * 0.06);
          ctx.lineTo(0, -h * 0.5);
          ctx.lineTo(w * k * 0.55, -h * 0.06);
          ctx.strokeStyle = warmStroke(0.048);
          ctx.stroke();
          ctx.restore();
        };

        drawIsoTile(
          canvas.width * 0.14,
          canvas.height * 0.3,
          100,
          58,
          0.12,
          0.00012
        );
        drawIsoTile(
          canvas.width * 0.88,
          canvas.height * 0.36,
          76,
          46,
          -0.18,
          -0.0001
        );
        drawIsoTile(
          canvas.width * 0.78,
          canvas.height * 0.72,
          88,
          50,
          0.22,
          0.00009
        );

        ctx.save();
        ctx.translate(canvas.width * 0.48, canvas.height * 0.52);
        ctx.rotate(time * 0.00008);
        ctx.beginPath();
        ctx.ellipse(
          0,
          0,
          canvas.width * 0.13,
          canvas.height * 0.06,
          0,
          0,
          Math.PI * 2
        );
        ctx.strokeStyle = warmStroke(0.052);
        ctx.lineWidth = 1;
        ctx.stroke();
        ctx.restore();
      }

      const waveR = lerp(74, 115, u);
      const waveG = lerp(144, 95, u);
      const waveB = lerp(226, 252, u);
      const ampScale = 1 + u * 0.65;

      waves.forEach((wave) => {
        ctx.beginPath();
        ctx.moveTo(0, wave.y);
        for (let x = 0; x < canvas.width; x += 8) {
          const y =
            wave.y +
            Math.sin(x * wave.frequency + time * wave.speed) *
              wave.amplitude *
              ampScale;
          ctx.lineTo(x, y);
        }
        ctx.strokeStyle = rgba(
          waveR,
          waveG,
          waveB,
          wave.opacity * (1 + u * 0.85)
        );
        ctx.lineWidth = 1 + u * 0.85;
        ctx.stroke();
      });

      const linkDist = lerp(92, 145, u);
      const linkDistSq = linkDist * linkDist;
      const pr = lerp(74, 185, u);
      const pg = lerp(144, 95, u);
      const pb = lerp(226, 255, u);

      const lr = lerp(74, 150, u);
      const lg = lerp(144, 108, u);
      const lb = lerp(226, 255, u);

      for (let i = 0; i < bins.length; i++) bins[i].length = 0;

      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        p.x += p.vx * (1 + u * 0.48);
        p.y += p.vy * (1 + u * 0.48);
        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;

        const bx = Math.min(bw - 1, Math.max(0, Math.floor(p.x / cellSize)));
        const by = Math.min(bh - 1, Math.max(0, Math.floor(p.y / cellSize)));
        bins[by * bw + bx].push(i);
      }

      ctx.lineWidth = 1;
      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        const bx = Math.min(bw - 1, Math.max(0, Math.floor(p.x / cellSize)));
        const by = Math.min(bh - 1, Math.max(0, Math.floor(p.y / cellSize)));

        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -1; dx <= 1; dx++) {
            const nbx = bx + dx;
            const nby = by + dy;
            if (nbx < 0 || nbx >= bw || nby < 0 || nby >= bh) continue;
            const list = bins[nby * bw + nbx];
            for (let k = 0; k < list.length; k++) {
              const j = list[k];
              if (j <= i) continue;
              const q = particles[j];
              const ddx = p.x - q.x;
              const ddy = p.y - q.y;
              const d2 = ddx * ddx + ddy * ddy;
              if (d2 >= linkDistSq) continue;
              const distance = Math.sqrt(d2);
              const opacity =
                (1 - distance / linkDist) * lerp(0.11, 0.2, u);
              ctx.beginPath();
              ctx.moveTo(p.x, p.y);
              ctx.lineTo(q.x, q.y);
              ctx.strokeStyle = rgba(lr, lg, lb, opacity);
              ctx.stroke();
            }
          }
        }

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size * (1 + u * 0.18), 0, Math.PI * 2);
        const a = Math.min(1, p.opacity * (0.88 + u * 0.38));
        ctx.fillStyle = rgba(pr, pg, pb, a);
        ctx.fill();
      }

      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      const ringRadius =
        80 +
        Math.sin(time * 0.001) * (22 + u * 24) +
        u * 18;

      ctx.save();
      ctx.translate(centerX, centerY);
      ctx.rotate(time * (0.0005 + u * 0.0028 + stick * 0.0008));

      const ringExtra = smoothstep((u - 0.22) / 0.32);
      for (let i = 0; i < 5; i++) {
        const ringFade = i < 3 ? 1 : ringExtra;
        if (ringFade < 0.01) continue;
        ctx.beginPath();
        ctx.arc(0, 0, ringRadius + i * 14, 0, Math.PI * 2);
        const baseA = (0.16 - i * 0.028) * (1 - u * 0.18) + u * 0.08;
        ctx.strokeStyle = rgba(
          lerp(123, 245, u),
          lerp(104, 125, u),
          lerp(238, 255, u),
          baseA * ringFade
        );
        ctx.lineWidth = 1.35 + u * 0.95;
        ctx.stroke();
      }
      ctx.restore();

      const streamAlpha = 0.2 * (1 - u * 0.72);
      if (streamAlpha > 0.02) {
        const streamCount = 4;
        for (let i = 0; i < streamCount; i++) {
          const streamX = (canvas.width / (streamCount + 1)) * (i + 1);
          const streamY = (time * 0.03 + i * 150) % canvas.height;
          ctx.beginPath();
          ctx.moveTo(streamX, streamY);
          ctx.lineTo(streamX, streamY + 40);
          ctx.strokeStyle = rgba(
            lerp(74, 130, u),
            lerp(144, 98, u),
            lerp(226, 248, u),
            streamAlpha
          );
          ctx.lineWidth = 2 + u * 0.75;
          ctx.stroke();
          for (let j = 0; j < 3; j++) {
            const pointY = streamY + j * 12;
            ctx.beginPath();
            ctx.arc(streamX, pointY, 3, 0, Math.PI * 2);
            ctx.fillStyle = rgba(
              lerp(123, 210, u),
              lerp(104, 128, u),
              lerp(238, 255, u),
              0.38 + u * 0.3
            );
            ctx.fill();
          }
        }
      }

      const gridMix = smoothstep((u - 0.06) / 0.44);
      if (gridMix > 0.004) {
        const gAlpha = gridMix * 0.17;
        const step = 72;
        ctx.strokeStyle = rgba(90, 66, 212, gAlpha);
        ctx.lineWidth = 1;
        ctx.beginPath();
        for (let x = 0; x <= canvas.width; x += step) {
          ctx.moveTo(x, 0);
          ctx.lineTo(x, canvas.height);
        }
        ctx.stroke();
        ctx.beginPath();
        for (let y = 0; y <= canvas.height; y += step) {
          ctx.moveTo(0, y);
          ctx.lineTo(canvas.width, y);
        }
        ctx.stroke();
      }

      const vigMix = smoothstep((u - 0.12) / 0.42);
      if (vigMix > 0.004) {
        const vig = ctx.createRadialGradient(
          centerX,
          centerY,
          canvas.height * 0.25,
          centerX,
          centerY,
          Math.max(canvas.width, canvas.height) * 0.72
        );
        const va = vigMix * 0.3 * (1 + stick * 0.45);
        vig.addColorStop(0, rgba(20, 15, 40, 0));
        vig.addColorStop(1, rgba(15, 10, 35, va));
        ctx.fillStyle = vig;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }

      ctx.restore();

      /* 스티커 호버(확대) 시 동일 프레임에서 time 진행을 키워 3D·파동·회전 전반이 빨라짐 */
      time += 16 * (1 + stick * 0.58);
      rafId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      cancelled = true;
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener('resize', resizeCanvas);
    };
  }, [scrollProgressRef]);

  return (
    <canvas
      ref={canvasRef}
      className="ar-background"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: -1,
        pointerEvents: 'none',
      }}
    />
  );
};

export default ARBackground;
